import { ORG_ROLE_DESCRIPTIONS, ORG_ROLE_LABELS } from '@hvac/contracts';
import { Alert, Button, Field, Input, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import {
  acceptInvitation,
  invitationPreviewQuery,
  registerWithInvitation,
} from '../../shared/api/membership-queries.js';
import { queryKeys, recordSignIn, setActiveOrganization } from '../../shared/api/queries.js';
import { useAuth } from '../../shared/auth/auth-context.js';
import { LoginForm } from './login-form.js';

/**
 * The invitation link, opened.
 *
 * It says what it is for before asking for anything: the business, the role,
 * the address it was sent to. Then one of three things, decided by who is
 * here: create the account the address belongs to; sign in to it; or, if
 * already signed in as that address, accept. Signed in as somebody else, it
 * says so and offers to sign out. The server refuses an acceptance from any
 * other address, so none of this is a courtesy.
 */
export function InvitePage() {
  const { token } = useParams({ from: '/invite/$token' });
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const preview = useQuery(invitationPreviewQuery(token));
  const [error, setError] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');

  const finish = async (organizationId: string) => {
    setActiveOrganization(organizationId);
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
    await navigate({ to: '/' });
  };

  const accept = useMutation({
    mutationFn: () => acceptInvitation(token),
    onSuccess: (result) => void finish(result.organizationId),
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The invitation could not be accepted.'),
  });

  const register = useMutation({
    mutationFn: async () => {
      const invited = preview.data;
      if (invited === undefined) throw new Error('The invitation has not loaded yet.');
      await registerWithInvitation(token, { fullName: fullName.trim(), password });
      await auth.signIn(invited.email, password);
      void recordSignIn();
      return acceptInvitation(token);
    },
    onSuccess: (result) => void finish(result.organizationId),
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The account could not be created.'),
  });

  const body = (() => {
    if (preview.isPending || auth.status === 'loading') return <Skeleton className="h-40 w-full" />;
    if (preview.isError) {
      return (
        <Alert tone="danger" title="This link is not recognised">
          It may have been copied incompletely, or withdrawn. Ask whoever invited you to send a new
          one.
        </Alert>
      );
    }
    const invited = preview.data;
    if (invited.status !== 'pending') {
      return (
        <Alert tone="warning" title={STATUS_TITLES[invited.status]}>
          {invited.status === 'accepted'
            ? 'Sign in as usual to open the books.'
            : 'Ask whoever invited you to send a new one.'}
        </Alert>
      );
    }

    const intro = (
      <div className="mb-6">
        <p className="text-sm text-content-muted">You were invited to</p>
        <h2 className="text-2xl font-semibold tracking-tight text-content">
          {invited.organizationName}
        </h2>
        <p className="mt-1 text-sm text-content-muted">
          as <span className="font-medium text-content">{ORG_ROLE_LABELS[invited.role]}</span>.{' '}
          {ORG_ROLE_DESCRIPTIONS[invited.role]}
        </p>
        <p className="mt-1 text-xs text-content-subtle">Sent to {invited.email}.</p>
      </div>
    );

    if (auth.status === 'authenticated') {
      const mine = (auth.email ?? '').toLowerCase() === invited.email.toLowerCase();
      return (
        <>
          {intro}
          {mine ? (
            <Button onClick={() => accept.mutate()} isLoading={accept.isPending}>
              Accept and open the books
            </Button>
          ) : (
            <Alert tone="warning" title={`You are signed in as ${auth.email ?? 'someone else'}`}>
              <p>This invitation is for {invited.email}. Sign out, then open the link again.</p>
              <div className="mt-2">
                <Button size="sm" variant="secondary" onClick={() => void auth.signOut()}>
                  Sign out
                </Button>
              </div>
            </Alert>
          )}
        </>
      );
    }

    if (invited.hasAccount) {
      return (
        <>
          {intro}
          <p className="mb-3 text-sm text-content-muted">
            That address already has an account. Sign in to accept.
          </p>
          <LoginForm
            onSubmit={async (values) => {
              if (values.email.trim().toLowerCase() !== invited.email.toLowerCase()) {
                throw new Error(`Sign in as ${invited.email} to accept this invitation.`);
              }
              await auth.signIn(values.email, values.password);
              void recordSignIn();
              const result = await acceptInvitation(token);
              await finish(result.organizationId);
            }}
          />
        </>
      );
    }

    return (
      <>
        {intro}
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            register.mutate();
          }}
        >
          <Field label="Your name" hint="As colleagues know you. It appears on what you sign.">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={fullName}
                autoComplete="name"
                onChange={(event) => setFullName(event.target.value)}
              />
            )}
          </Field>
          <Field label="Choose a password" hint="At least ten characters.">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                type="password"
                value={password}
                autoComplete="new-password"
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>
          <Button
            type="submit"
            isLoading={register.isPending}
            disabled={fullName.trim().length < 2 || password.length < 10}
          >
            Create my account and accept
          </Button>
        </form>
      </>
    );
  })();

  return (
    <main className="flex min-h-dvh items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <p className="mb-8 flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-[9px] bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]">
            P
          </span>
          <span className="text-sm font-semibold tracking-tight text-content">
            Pathara <span className="text-brand-600">Finance</span>
          </span>
        </p>
        {error === null ? null : (
          <div className="mb-4">
            <Alert tone="danger">{error}</Alert>
          </div>
        )}
        {body}
      </div>
    </main>
  );
}

const STATUS_TITLES = {
  pending: '',
  accepted: 'This invitation was already accepted',
  revoked: 'This invitation was withdrawn',
  expired: 'This invitation has expired',
} as const;
